"use client";

import { isNativeIOS } from "./runtime";

export type AppleMusicConfig = {
  developerToken: string;
  storefront: string;
  appName: string;
  appBuild: string;
};

export type AppleMusicInstance = {
  musicUserToken?: string | null;
  storefrontId?: string | null;
  volume?: number;
  isPlaying?: boolean;
  playbackState?: number | string;
  nowPlayingItem?: unknown;
  currentPlaybackTime?: number;
  currentPlaybackDuration?: number;
  authorize?: () => Promise<string>;
  unauthorize?: () => Promise<void> | void;
  setQueue?: (queueDescriptor: Record<string, unknown>) => Promise<unknown>;
  play?: () => Promise<void>;
  pause?: () => Promise<void>;
  stop?: () => Promise<void>;
  skipToNextItem?: () => Promise<void>;
  skipToPreviousItem?: () => Promise<void>;
  seekToTime?: (seconds: number) => Promise<void>;
  changeToMediaAtIndex?: (index: number) => Promise<void> | void;
  addEventListener?: (
    eventName: string,
    listener: EventListenerOrEventListenerObject
  ) => void;
  removeEventListener?: (
    eventName: string,
    listener: EventListenerOrEventListenerObject
  ) => void;
  player?: {
    currentPlaybackTime?: number;
    currentPlaybackDuration?: number;
    playbackState?: number | string;
    isPlaying?: boolean;
    queue?: {
      position?: number;
    };
  } | null;
};

type AppleMusicKitGlobal = {
  configure: (config: {
    developerToken: string;
    app: {
      name: string;
      build: string;
    };
  }) => void;
  getInstance: () => AppleMusicInstance;
  Events?: Record<string, string>;
};

declare global {
  interface Window {
    MusicKit?: AppleMusicKitGlobal;
  }
}

const APPLE_MUSIC_KIT_SCRIPT_ID = "imags-apple-music-kit";
const APPLE_MUSIC_KIT_SCRIPT_URL =
  "https://js-cdn.music.apple.com/musickit/v3/musickit.js";

let appleMusicKitScriptPromise: Promise<void> | null = null;
let appleMusicKitConfigPromise: Promise<AppleMusicConfig> | null = null;
let configuredDeveloperToken: string | null = null;
let cachedAppleMusicInstance: AppleMusicInstance | null = null;

function sleep(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function waitForMusicKitGlobal(maxWaitMs = 2500) {
  const started = Date.now();
  while (Date.now() - started < maxWaitMs) {
    if (window.MusicKit) return true;
    await sleep(50);
  }
  return Boolean(window.MusicKit);
}

function isAppleMusicInstance(value: unknown): value is AppleMusicInstance {
  return Boolean(value && typeof value === "object");
}

function asRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object") return {};
  return value as Record<string, unknown>;
}

function parseAppleMusicConfig(payload: unknown): AppleMusicConfig {
  const data = asRecord(payload);
  const developerToken =
    typeof data.developerToken === "string" ? data.developerToken.trim() : "";
  if (!developerToken) {
    throw new Error("Apple Music developer token is missing");
  }

  const storefront =
    typeof data.storefront === "string" && data.storefront.trim()
      ? data.storefront.trim()
      : "us";
  const appName =
    typeof data.appName === "string" && data.appName.trim()
      ? data.appName.trim()
      : "IMAGS";
  const appBuild =
    typeof data.appBuild === "string" && data.appBuild.trim()
      ? data.appBuild.trim()
      : "1.0.0";

  return { developerToken, storefront, appName, appBuild };
}

export async function fetchAppleMusicConfig() {
  if (appleMusicKitConfigPromise) {
    return appleMusicKitConfigPromise;
  }

  appleMusicKitConfigPromise = fetch("/api/apple/config", {
    credentials: "include",
  })
    .then(async (res) => {
      const text = await res.text().catch(() => "");
      let payload: unknown = null;
      try {
        payload = text ? JSON.parse(text) : null;
      } catch {
        payload = null;
      }

      if (!res.ok) {
        const parsed = asRecord(payload);
        const message =
          typeof parsed.error === "string" && parsed.error.trim()
            ? parsed.error
            : `Failed to load Apple Music config (${res.status})`;
        const details =
          typeof parsed.details === "string" && parsed.details.trim()
            ? parsed.details.trim()
            : "";
        const hint =
          typeof parsed.hint === "string" && parsed.hint.trim()
            ? parsed.hint.trim()
            : "";
        const suffix = [details, hint].filter(Boolean).join(" | ");
        throw new Error(suffix ? `${message}: ${suffix}` : message);
      }

      return parseAppleMusicConfig(payload);
    })
    .catch((err) => {
      appleMusicKitConfigPromise = null;
      throw err;
    });

  return appleMusicKitConfigPromise;
}

export async function loadAppleMusicKitScript() {
  if (typeof window === "undefined") {
    throw new Error("Apple Music Kit is only available in the browser");
  }
  if (window.MusicKit) return;
  if (appleMusicKitScriptPromise) {
    return appleMusicKitScriptPromise;
  }

  appleMusicKitScriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(
      APPLE_MUSIC_KIT_SCRIPT_ID
    ) as HTMLScriptElement | null;

    if (existing) {
      if (window.MusicKit) {
        resolve();
        return;
      }

      let settled = false;
      const settle = (err?: Error) => {
        if (settled) return;
        settled = true;
        if (err) {
          reject(err);
          return;
        }
        resolve();
      };

      const pollForGlobal = async () => {
        const ready = await waitForMusicKitGlobal(2500);
        if (ready) {
          settle();
          return;
        }
        settle(new Error("Apple Music Kit did not initialize in time"));
      };

      existing.addEventListener(
        "load",
        () => {
          void pollForGlobal();
        },
        { once: true }
      );
      existing.addEventListener(
        "error",
        () => settle(new Error("Failed to load Apple Music Kit")),
        { once: true }
      );

      // If the script finished loading before listeners were attached,
      // rely on polling so first-click auth still works.
      void pollForGlobal();
      return;
    }

    const script = document.createElement("script");
    script.id = APPLE_MUSIC_KIT_SCRIPT_ID;
    script.src = APPLE_MUSIC_KIT_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Apple Music Kit"));
    document.body.appendChild(script);
  }).catch((err) => {
    appleMusicKitScriptPromise = null;
    throw err;
  });

  await appleMusicKitScriptPromise;
  const ready = await waitForMusicKitGlobal(2500);
  if (!ready || !window.MusicKit) {
    throw new Error("Apple Music Kit is unavailable");
  }
}

export async function ensureAppleMusicKitInstance() {
  const config = await fetchAppleMusicConfig();
  await loadAppleMusicKitScript();

  const globalMusicKit = window.MusicKit;
  if (!globalMusicKit) {
    throw new Error("Apple Music Kit is unavailable");
  }

  if (configuredDeveloperToken !== config.developerToken) {
    globalMusicKit.configure({
      developerToken: config.developerToken,
      app: {
        name: config.appName,
        build: config.appBuild,
      },
    });
    configuredDeveloperToken = config.developerToken;
  }

  const getInstanceSafe = () => {
    try {
      return globalMusicKit.getInstance?.() ?? null;
    } catch {
      return null;
    }
  };

  let instance = getInstanceSafe();
  if (!isAppleMusicInstance(instance)) {
    // If the singleton wasn't ready, retry after explicit configure.
    globalMusicKit.configure({
      developerToken: config.developerToken,
      app: {
        name: config.appName,
        build: config.appBuild,
      },
    });
    configuredDeveloperToken = config.developerToken;

    for (let attempt = 0; attempt < 8; attempt += 1) {
      await sleep(50);
      instance = getInstanceSafe();
      if (isAppleMusicInstance(instance)) break;
    }
  }

  if (!isAppleMusicInstance(instance)) {
    throw new Error("Apple Music instance is unavailable");
  }

  cachedAppleMusicInstance = instance;

  return {
    musicKit: globalMusicKit,
    music: instance,
    config,
  };
}

export function getCachedAppleMusicInstance() {
  return cachedAppleMusicInstance;
}

export async function warmAppleMusicKit() {
  if (isNativeIOS()) return null;
  return ensureAppleMusicKitInstance();
}
